import {
  CompareMeasurementMeta, CompareTypeAdapter, CompareVariable,
} from './registry/compare-type-adapter.model';

export interface CatalogVariable extends CompareVariable {
  /** How many selected measurements of this type offer the variable. */
  available: number;
  /** How many selected measurements of this type there are. */
  total: number;
}
export interface CatalogSection { section: string; sectionLabelKey: string; variables: CatalogVariable[] }
export interface CatalogType { type: string; supported: boolean; total: number; sections: CatalogSection[] }

/**
 * Variable catalog for a set of selected measurements, grouped type → section.
 * Order is first-seen; variable keys are namespaced per type (no cross-type merge).
 */
export function buildVariableCatalog(
  metas: CompareMeasurementMeta[],
  registry: Record<string, CompareTypeAdapter>,
): CatalogType[] {
  const byType = new Map<string, CompareMeasurementMeta[]>();
  for (const m of metas) {
    if (!byType.has(m.measurementType)) byType.set(m.measurementType, []);
    byType.get(m.measurementType)!.push(m);
  }

  const out: CatalogType[] = [];
  for (const [type, group] of byType) {
    const adapter = registry[type];
    if (!adapter) {
      out.push({ type, supported: false, total: group.length, sections: [] });
      continue;
    }
    const sections = new Map<string, Map<string, CatalogVariable>>();
    for (const m of group) {
      for (const v of adapter.variables(m)) {
        if (!sections.has(v.section)) sections.set(v.section, new Map());
        const vars = sections.get(v.section)!;
        const existing = vars.get(v.key);
        if (existing) existing.available += 1;
        else vars.set(v.key, { ...v, available: 1, total: group.length });
      }
    }
    out.push({
      type, supported: true, total: group.length,
      sections: [...sections].map(([section, vars]) => ({
        section, sectionLabelKey: adapter.sectionLabelKey(section), variables: [...vars.values()],
      })),
    });
  }
  return out;
}
